export type ScheduleSettingsSection = {
  id: string;
  navLabel: string;
  title: string;
  path: string;
  description: string;
  completion: number;
  readinessLabel: string;
  workspaceTitle: string;
  workspaceDescription: string;
  metrics: Array<{
    label: string;
    value: string;
    tone: 'primary' | 'success' | 'warning' | 'info';
  }>;
  checklist: string[];
};

export const scheduleSettingsSections: ScheduleSettingsSection[] = [
  {
    id: 'overview',
    navLabel: 'Overview',
    title: 'Scheduling Overview',
    path: '/scheduling/overview',
    description: 'Review available setup data, missing prerequisites, and the sections that already have live controls.',
    completion: 42,
    readinessLabel: 'Availability summary',
    workspaceTitle: 'Current state',
    workspaceDescription: 'Use this overview to confirm which schedule settings areas already have editable tools and which still depend on upstream data or support.',
    metrics: [
      { label: 'Sections planned', value: '11', tone: 'primary' },
      { label: 'Routes live', value: '11', tone: 'success' },
      { label: 'Readiness blockers', value: '3', tone: 'warning' },
    ],
    checklist: [
      'Start with Imports when the tenant still needs workbook data loaded.',
      'Use Locations, Services, and People for the records that can already be managed here.',
      'Review blockers before expecting schedule validation or rule controls to be available.',
    ],
  },
  {
    id: 'imports',
    navLabel: 'Imports',
    title: 'Imports',
    path: '/scheduling/settings/imports',
    description: 'Stage spreadsheet uploads, preview mappings, and surface validation before committing schedule inputs.',
    completion: 64,
    readinessLabel: 'Import staging available',
    workspaceTitle: 'Import workspace',
    workspaceDescription: 'Use the import wizard below to upload workbooks, review detected worksheet types, map columns, validate preview rows, and stage records for this tenant.',
    metrics: [
      { label: 'Accepted workbook types', value: '3', tone: 'success' },
      { label: 'Preview rows', value: 'Client parsed', tone: 'info' },
      { label: 'Import runs', value: '0', tone: 'primary' },
    ],
    checklist: [
      'Verify the standard workbook headers for each source sheet.',
      'Use the preview counts to confirm the workbook loaded the rows you expected.',
      'Resolve blocking mapping issues before saving worksheet data for the tenant.',
      'Treat imported rows here as staged source data, not final schedule output.',
    ],
  },
  {
    id: 'locations',
    navLabel: 'Locations',
    title: 'Locations',
    path: '/scheduling/settings/locations',
    description: 'Manage company and Schedule Group locations together with their Chronos-derived drafts.',
    completion: 62,
    readinessLabel: 'Structure tools available',
    workspaceTitle: 'Locations',
    workspaceDescription: 'Compare active locations with group-scoped drafts and their evidence.',
    metrics: [
      { label: 'Active locations', value: '0', tone: 'primary' },
      { label: 'Services linked', value: '0', tone: 'info' },
      { label: 'Coverage warnings', value: '0', tone: 'warning' },
    ],
    checklist: [
      'Company Admin retains tenant-wide location naming authority.',
      'Schedule Admin locations remain scoped to the selected Schedule Group.',
      'Review imported location drafts before activation.',
    ],
  },
  {
    id: 'services',
    navLabel: 'Services',
    title: 'Services',
    path: '/scheduling/settings/services',
    description: 'Manage the services used by the selected Schedule Group.',
    completion: 62,
    readinessLabel: 'Service tools available',
    workspaceTitle: 'Services',
    workspaceDescription: 'Compare active services with group-scoped drafts and their evidence.',
    metrics: [
      { label: 'Services loaded', value: '0', tone: 'primary' },
      { label: 'Locations available', value: '0', tone: 'info' },
      { label: 'Provider dependencies', value: 'Pending', tone: 'warning' },
    ],
    checklist: [
      'Keep service codes unique within the Schedule Group.',
      'Link services only to locations available to the selected group.',
      'Review imported service drafts before activation.',
    ],
  },
  {
    id: 'providers',
    navLabel: 'People',
    title: 'People',
    path: '/scheduling/settings/providers',
    description: 'Manage the people registry for the selected Schedule Group.',
    completion: 62,
    readinessLabel: 'People tools available',
    workspaceTitle: 'People',
    workspaceDescription: 'Manage approved people records and compare imported drafts with their evidence.',
    metrics: [
      { label: 'People loaded', value: '0', tone: 'primary' },
      { label: 'People active', value: '0', tone: 'success' },
      { label: 'Alias conflicts', value: '0', tone: 'warning' },
    ],
    checklist: [
      'Keep each person code unique within the Schedule Group.',
      'Identity approval does not grant eligibility or schedulability.',
      'Review imported people drafts before activation.',
    ],
  },
  {
    id: 'roles-shifts',
    navLabel: 'Roles & Shifts',
    title: 'Roles & Shifts',
    path: '/scheduling/settings/roles-shifts',
    description: 'Manage roles and shift definitions for the selected Schedule Group.',
    completion: 62,
    readinessLabel: 'Role and shift tools available',
    workspaceTitle: 'Roles & Shifts',
    workspaceDescription: 'Review active role and shift definitions alongside imported drafts.',
    metrics: [
      { label: 'Role families', value: '0', tone: 'primary' },
      { label: 'Shift templates', value: '0', tone: 'info' },
      { label: 'Alias bundles', value: '0', tone: 'warning' },
    ],
    checklist: [
      'Keep role and shift codes stable for downstream scheduling rules.',
      'Review aliases before approving imported definitions.',
      'Activate approved drafts only as a separate scheduler action.',
    ],
  },
  {
    id: 'eligibility',
    navLabel: 'Eligibility',
    title: 'Eligibility',
    path: '/scheduling/settings/eligibility',
    description: 'Map which providers can cover which services, locations, and shift types, including exclusions and overrides.',
    completion: 24,
    readinessLabel: 'Unavailable',
    workspaceTitle: 'Eligibility matrix',
    workspaceDescription: 'Eligibility editing is not available in this workspace yet. Confirm providers, services, and locations first so the required relationships are ready when this section opens.',
    metrics: [
      { label: 'Eligibility rows', value: '0', tone: 'primary' },
      { label: 'Hard exclusions', value: '0', tone: 'warning' },
      { label: 'Validated mappings', value: '0%', tone: 'info' },
    ],
    checklist: [
      'Keep provider, service, and location records current in their live sections.',
      'Hold eligibility source sheets in Imports for review and traceability.',
      'This page will remain informational until eligibility controls are enabled.',
    ],
  },
  {
    id: 'time-off',
    navLabel: 'Time Off',
    title: 'Time Off & Admin Time',
    path: '/scheduling/settings/time-off',
    description: 'Track PTO, admin time, and unavailability windows that should constrain schedule generation.',
    completion: 26,
    readinessLabel: 'Unavailable',
    workspaceTitle: 'Availability controls',
    workspaceDescription: 'Time off and admin-time controls are not available here yet. Track those constraints in your existing source process until this section is opened for editing.',
    metrics: [
      { label: 'Blocked dates', value: '0', tone: 'primary' },
      { label: 'Admin blocks', value: '0', tone: 'info' },
      { label: 'Conflict warnings', value: '0', tone: 'success' },
    ],
    checklist: [
      'Keep provider roster records current before tracking availability exceptions.',
      'Use Imports for any workbook rows that need to be preserved for later review.',
      'Expect schedule runs to rely on external availability tracking until this section is available.',
    ],
  },
  {
    id: 'targets',
    navLabel: 'Targets',
    title: 'Targets & Fairness',
    path: '/scheduling/settings/targets',
    description: 'Capture call targets, fairness requirements, and balancing rules that will guide generation and reporting.',
    completion: 22,
    readinessLabel: 'Unavailable',
    workspaceTitle: 'Target controls',
    workspaceDescription: 'Call targets and fairness settings are not editable in this workspace yet. Use this section as a reminder that balancing rules are still managed outside CARES.',
    metrics: [
      { label: 'Target sets', value: '0', tone: 'primary' },
      { label: 'Fairness rules', value: '0', tone: 'warning' },
      { label: 'Variance alerts', value: '0', tone: 'info' },
    ],
    checklist: [
      'Keep any provider target source sheets in Imports for reference.',
      'Do not assume fairness settings shown here are active scheduling controls.',
      'Continue using the current external process for target and balancing decisions.',
    ],
  },
  {
    id: 'rules',
    navLabel: 'Rules',
    title: 'Rules & Bundles',
    path: '/scheduling/settings/rules',
    description: 'Organize hard constraints, soft constraints, and bundled rule sets that affect schedule generation outcomes.',
    completion: 18,
    readinessLabel: 'Unavailable',
    workspaceTitle: 'Constraint bundles',
    workspaceDescription: 'Schedule rule editing is not available here yet. Any hard or soft scheduling constraints still need to be tracked outside this workspace.',
    metrics: [
      { label: 'Hard rules', value: '0', tone: 'primary' },
      { label: 'Soft rules', value: '0', tone: 'info' },
      { label: 'Bundle presets', value: '0', tone: 'warning' },
    ],
    checklist: [
      'Document current scheduling constraints in the external process your team already uses.',
      'Use Validation for blocker visibility once readiness checks are available.',
      'Do not rely on this page to enforce scheduling policy today.',
    ],
  },
];
